import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { useAuth } from '../auth/AuthContext';
import { useIdleTimer } from '../hooks/useIdleTimer';
import { SessionTimeoutWarning } from '../components/common/SessionTimeoutWarning';

// Configuration from environment variables with defaults
const SESSION_TIMEOUT = parseInt(
  import.meta.env.VITE_SESSION_TIMEOUT || '900000', // 15 minutes default
  10
);
const WARNING_TIME = parseInt(
  import.meta.env.VITE_SESSION_WARNING_TIME || '60000', // 1 minute warning default
  10
);
const SESSION_TIMEOUT_ENABLED = 
  import.meta.env.VITE_SESSION_TIMEOUT_ENABLED !== 'false'; // Enabled by default

const normalizeToPortalRoot = (): void => {
  window.history.replaceState(null, '', '/');
};

interface SessionTimeoutContextValue {
  /** Whether the session timeout feature is enabled */
  isEnabled: boolean;
  /** Remaining time until session expires (ms) */
  remainingTime: number;
  /** Whether user is currently in warning state */
  isWarning: boolean;
  /** Whether user has been logged out due to inactivity */
  isTimedOut: boolean;
  /** Manually reset the session timer */
  resetSession: () => void;
  /** Pause session timeout (e.g., during important operations) */
  pauseTimeout: () => void;
  /** Resume session timeout */
  resumeTimeout: () => void;
  /** Whether timeout is currently paused */
  isPaused: boolean;
  /** Configured timeout duration (ms) */
  timeoutDuration: number;
  /** Configured warning time before timeout (ms) */
  warningDuration: number;
}

const SessionTimeoutContext = createContext<SessionTimeoutContextValue | undefined>(undefined);

/**
 * Hook to access session timeout context
 */
export const useSessionTimeout = (): SessionTimeoutContextValue => {
  const context = useContext(SessionTimeoutContext);
  if (!context) {
    throw new Error('useSessionTimeout must be used within a SessionTimeoutProvider');
  }
  return context;
};

interface SessionTimeoutProviderProps {
  children: ReactNode;
  /** Override timeout duration (ms) */
  timeout?: number;
  /** Override warning time (ms) */
  warningTime?: number;
  /** Override enabled state */
  enabled?: boolean;
}

/**
 * Provider component that manages session timeout and shows warning modal
 * 
 * Wraps the app to:
 * 1. Track user activity
 * 2. Show warning before session expires
 * 3. Auto-logout inactive users
 * 
 * @example
 * ```tsx
 * <AuthProvider>
 *   <SessionTimeoutProvider>
 *     <App />
 *   </SessionTimeoutProvider>
 * </AuthProvider>
 * ```
 */
export const SessionTimeoutProvider: React.FC<SessionTimeoutProviderProps> = ({
  children,
  timeout = SESSION_TIMEOUT,
  warningTime = WARNING_TIME,
  enabled = SESSION_TIMEOUT_ENABLED,
}) => {
  const { isAuthenticated, logout } = useAuth();
  const [showWarning, setShowWarning] = useState(false);
  const [isTimedOut, setIsTimedOut] = useState(false);

  const handleWarning = useCallback(() => {
    if (isAuthenticated) {
      setShowWarning(true);
    }
  }, [isAuthenticated]);

  const handleIdle = useCallback(async () => {
    if (isAuthenticated) {
      setShowWarning(false);
      setIsTimedOut(true);
      try {
        await logout({ localOnly: true });
        normalizeToPortalRoot();
        // Optionally redirect or show message
        console.log('Session expired due to inactivity');
      } catch (error) {
        console.error('Error during auto-logout:', error);
      }
    }
  }, [isAuthenticated, logout]);

  const {
    resetTimer,
    isWarning,
    remainingTime,
    pause,
    resume,
    isPaused,
  } = useIdleTimer({
    timeout,
    warningTime,
    onWarning: handleWarning,
    onIdle: handleIdle,
    enabled: enabled && isAuthenticated,
  });

  const handleExtendSession = useCallback(() => {
    setShowWarning(false);
    resetTimer();
  }, [resetTimer]);

  const handleLogoutNow = useCallback(async () => {
    setShowWarning(false);
    try {
      await logout({ localOnly: true });
      normalizeToPortalRoot();
    } catch (error) {
      console.error('Error during logout:', error);
    }
  }, [logout]);

  const value: SessionTimeoutContextValue = {
    isEnabled: enabled,
    remainingTime,
    isWarning: showWarning,
    isTimedOut,
    resetSession: resetTimer,
    pauseTimeout: pause,
    resumeTimeout: resume,
    isPaused,
    timeoutDuration: timeout,
    warningDuration: warningTime,
  };

  return (
    <SessionTimeoutContext.Provider value={value}>
      {children}
      {isAuthenticated && (
        <SessionTimeoutWarning
          visible={showWarning}
          remainingTime={remainingTime}
          onExtendSession={handleExtendSession}
          onLogout={handleLogoutNow}
        />
      )}
    </SessionTimeoutContext.Provider>
  );
};

export default SessionTimeoutProvider;
